import { CheckIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useState } from 'react';
import { Button, CircleSpinner } from '../../../components/Button';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { Modal } from '../../../components/Modal';
import { useSubscriptionTypeTranslations } from '../../../hooks/translations/useSubscriptionTypeTranslations';
import { useFormatDate } from '../../../hooks/useFormatDate';
import useOpen from '../../../hooks/useOpen';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { T, useT } from '../../../translation/src';
import {
  OrganisationPlanChangePreviewQuery,
  PlanChangeKind,
  PlanChangeProblem,
  SubscriptionType,
  useOrganisationBillingQuery,
  useOrganisationChangePlanMutation,
  useOrganisationLinkBillingMutation,
  useOrganisationPlanChangePreviewQuery,
} from '../../../types/graphqlTypes';
import { POINTORAMA_TERMS_URL } from '../../../utils/constants';
import { dayAfterTermEnd } from '../../../utils/dates';
import { formatPrice } from '../../../utils/formatPrice';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';
import { BillingInfoCard, BillingInfoModal } from '../BillingInfoCard';
import { ContactSalesModal } from '../ContactSalesModal';
import { useOrderConfirmation } from '../OrderConfirmationModal';

type Preview = OrganisationPlanChangePreviewQuery['organisationPlanChangePreview'];

// The plans a customer can move to by themselves, cheapest first.
const PLANS = [SubscriptionType.Launch, SubscriptionType.Grow, SubscriptionType.Scale] as const;

const usePlanPreview = (organisationId: string, subscriptionType: SubscriptionType, skip = false) =>
  // A refetch (after a link, a saved address, a change) counts as loading too: the dialog holds its button
  // while the price on it may be about to change.
  useOrganisationPlanChangePreviewQuery({ variables: { organisationId, subscriptionType }, skip, fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

const Section: React.FC2<{ title: React.ReactNode }> = ({ title, children, className }) => (
  <div className={classNames('flex flex-col gap-1 rounded-md border border-gray-300 p-3 dark:border-gray-600', className)}>
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</span>
    {children}
  </div>
);
const Row: React.FC2<{ label: React.ReactNode; emphasis?: boolean }> = ({ label, emphasis, children, className }) => (
  <div className={classNames('flex items-baseline justify-between gap-4 text-sm', emphasis && 'font-semibold', className)}>
    <span className={classNames(!emphasis && 'text-gray-600 dark:text-gray-300')}>{label}</span>
    <span className="text-right">{children}</span>
  </div>
);
const Hint: React.FC2 = ({ children, className }) => <span className={classNames('text-xs text-gray-500', className)}>{children}</span>;

// The confirmation: what the plan gives, from when, and what it costs — and why that amount.
// The dialog reads the live simulation for its plan, not the snapshot taken when the card was clicked: what
// happens in it (billing details completed, a link made) changes the price, and the snapshot would keep
// showing the old one while the server would charge the new. The snapshot only bridges the first load.
const ChangePlanDialog: React.FC<{ open: boolean; onClose: () => void; organisationId: string; preview: Preview; onChanged: () => void }> = ({ open, onClose, organisationId, preview: picked, onChanged }) => {
  const { data, previousData, loading: previewLoading } = usePlanPreview(organisationId, picked.subscriptionType, !open);
  const live = data?.organisationPlanChangePreview ?? previousData?.organisationPlanChangePreview;
  const preview = live?.subscriptionType === picked.subscriptionType ? live : picked;
  return <ChangePlanDialogContent open={open} onClose={onClose} organisationId={organisationId} preview={preview} previewLoading={previewLoading} onChanged={onChanged} />;
};

const ChangePlanDialogContent: React.FC<{ open: boolean; onClose: () => void; organisationId: string; preview: Preview; previewLoading: boolean; onChanged: () => void }> = ({
  open,
  onClose,
  organisationId,
  preview,
  previewLoading,
  onChanged,
}) => {
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const { showToast } = useToastNotifications();
  const { show: showConfirmation } = useOrderConfirmation();
  const failed = useT('could not change the plan', { swc: true });
  // The organisation comes back from the mutation (the header reads the cache); only the cards' simulations
  // have to be asked again — not the page's own queries, which reloaded the whole page.
  const [changePlan, { loading }] = useOrganisationChangePlanMutation({ refetchQueries: ['organisationPlanChangePreview'] });

  // No sale without billing details: the card asks for them and the button waits. Only a definite "incomplete"
  // blocks; an organisation not known to sales yet is pointed to them (the same as when buying projects).
  const { data: billingData, error: billingError, refetch: refetchBilling } = useOrganisationBillingQuery({ variables: { organisationId }, skip: !open, fetchPolicy: 'cache-and-network' });
  const billing = billingError ? undefined : billingData?.organisationBilling;
  const billingMissing = !!billing?.enabled && !billing.complete;
  const { open: billingModalOpen, onOpen: openBillingModal, onClose: closeBillingModal } = useOpen();
  // The price depends on whether the organisation can be invoiced, which the link decides: the simulation
  // fetched when the dialog opened was made before it, and would keep showing a locked price of 0.
  const [linkBilling, { loading: linking, called: linkTried, reset: resetLink }] = useOrganisationLinkBillingMutation({ refetchQueries: ['organisationPlanChangePreview'] });
  const unlinked = !!billing?.enabled && !billing.linked;
  useEffect(() => {
    if (!open) {
      if (!linkTried) return;
      const timeout = setTimeout(resetLink, 300);
      return () => clearTimeout(timeout);
    }
    if (unlinked && !linkTried) linkBilling({ variables: { organisationId } }).catch(() => undefined);
  }, [linkBilling, linkTried, open, organisationId, resetLink, unlinked]);
  const linkPending = unlinked && (!linkTried || linking);
  const linkFailed = unlinked && linkTried && !linking;
  // The terms, ticked for a sale (see `sells` below).
  const [agreed, setAgreed] = useState(false);
  // Closing forgets the tick: the next order starts unticked.
  const close = useCallback(() => {
    setAgreed(false);
    onClose();
  }, [onClose]);
  const onAgree = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setAgreed(event.target.checked), []);
  const { open: contactOpen, onOpen: openContact, onClose: closeContact } = useOpen();
  const onContactSales = useCallback(() => {
    close();
    openContact();
  }, [close, openContact]);

  const plan = plans[preview.subscriptionType];
  const upgrade = preview.kind === PlanChangeKind.Upgrade;
  const starts = preview.kind === PlanChangeKind.NewSubscription;
  const downgrade = preview.kind === PlanChangeKind.ScheduledDowngrade;
  // The current plan again, with a change scheduled: keeping it calls the change off.
  const keeps = preview.kind === PlanChangeKind.Unchanged;
  const scheduledPlan = preview.current.scheduledType ? plans[preview.current.scheduledType] : '';
  const scheduledOn = preview.current.scheduledAt ? formatDay(dayAfterTermEnd(preview.current.scheduledAt)) : '';
  // A downgrade takes effect at the end of the running term (its effectiveAt); the plan it moves to is `next`.
  const renewal = downgrade ? formatDay(dayAfterTermEnd(preview.effectiveAt)) : preview.endDate ? formatDay(dayAfterTermEnd(preview.endDate)) : undefined;
  const from = formatDay(dayAfterTermEnd(preview.effectiveAt));
  const target = downgrade && preview.next ? preview.next : preview.after;
  const paused = preview.problems.includes(PlanChangeProblem.SubscriptionPaused);
  // A sale (a start, an upgrade) needs the terms ticked; a downgrade or keeping the plan sells nothing.
  const sells = !downgrade && !keeps;

  const onConfirm = useCallback(async () => {
    try {
      await changePlan({ variables: { organisationId, subscriptionType: preview.subscriptionType } });
    } catch (e) {
      if (graphqlErrorCode(e) === 'BILLING_INFO_MISSING') await refetchBilling();
      else showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      return;
    }
    onChanged();
    close();
    // A sale gets its confirmation: usable now, invoiced once sales has checked it. A downgrade sells nothing.
    if (sells)
      showConfirmation({
        organisationId,
        what: upgrade ? <T _str="Upgrade to {plan}" args={{ plan }} /> : <T _str="{plan} subscription" args={{ plan }} />,
        ready:
          upgrade && preview.extraCredits > 0 ? (
            <T _str="Your plan is upgraded now. {credits} extra projects are added to this month." args={{ credits: preview.extraCredits }} />
          ) : upgrade ? (
            <T _str="Your plan is upgraded now." />
          ) : preview.extraCredits > 0 ? (
            <T _str="Your plan is active now. Your first {credits} monthly projects are ready." args={{ credits: preview.extraCredits }} />
          ) : (
            <T _str="Your plan is active now." />
          ),
        totalCents: preview.chargedCents,
        invoiceEmail: billing?.info?.email,
      });
  }, [billing?.info?.email, changePlan, failed, onChanged, close, organisationId, plan, preview.chargedCents, preview.extraCredits, preview.subscriptionType, refetchBilling, sells, showConfirmation, showToast, upgrade]);

  return (
    <>
      <Modal.Container open={open} onClose={close} panelClassName="w-full max-w-lg">
        <Modal.Header className="mb-2!" onClose={close}>
          {keeps ? <T _str="Keep {plan}" args={{ plan }} /> : downgrade ? <T _str="Switch to {plan} at renewal" args={{ plan }} /> : starts ? <T _str="Start {plan}" args={{ plan }} /> : <T _str="Upgrade to {plan}" args={{ plan }} />}
        </Modal.Header>
        {linkPending ? (
          <Modal.Body className="flex h-40 items-center justify-center">
            <CircleSpinner className="h-6 w-6" />
          </Modal.Body>
        ) : linkFailed ? (
          <>
            <Modal.Body className="flex flex-col gap-2 text-sm">
              <span className="font-semibold">
                <T _str="Your organisation is not set up for ordering yet." />
              </span>
              <span className="text-gray-500">
                <T _str="Contact sales and we will get you started, so you can change your plan here." />
              </span>
            </Modal.Body>
            <Modal.Footer className="justify-between">
              <Button variant="secondary" onClick={close}>
                <T _str="cancel" swc />
              </Button>
              <Button variant="primary" onClick={onContactSales}>
                <T _str="contact sales" swc />
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Body className="flex flex-col gap-3">
              <Section title={<T _str="what you get" swc />}>
                <Row label={<span className="font-medium">{plan}</span>}>
                  <T _str="{credits} projects / month · {price} / year" args={{ credits: target.monthlyCredits, price: formatPrice(target.yearlyPriceCents) }} />
                </Row>
                {keeps ? (
                  <Hint>
                    <T _str="This cancels the switch to {scheduled} on {date}: your plan runs on as it is and renews as before." args={{ scheduled: scheduledPlan, date: scheduledOn }} />
                  </Hint>
                ) : downgrade ? (
                  <Hint>
                    <T _str="Nothing changes until {date}. Your current plan and its monthly projects run to the end of the term. From then on you are on {plan}, billed at renewal." args={{ date: renewal ?? '', plan }} />
                  </Hint>
                ) : (
                  <>
                    <Row label={<T _str="from" swc />}>{from}</Row>
                    {renewal && <Row label={<T _str="renews" swc />}>{renewal}</Row>}
                    {preview.extraCredits > 0 && preview.extraCreditsExpireAt && (
                      <Hint>
                        {upgrade ? (
                          <T _str="+{credits} projects are added to this month right away, valid until {date}. Your renewal date stays the same." args={{ credits: preview.extraCredits, date: formatDay(preview.extraCreditsExpireAt) }} />
                        ) : (
                          <T _str="Your first {credits} monthly projects are granted right away." args={{ credits: preview.extraCredits }} />
                        )}
                      </Hint>
                    )}
                  </>
                )}
              </Section>
              <Section title={<T _str="price" swc />}>
                {keeps ? (
                  <Row label={<T _str="billed now" swc />} emphasis>
                    {formatPrice(0)}
                  </Row>
                ) : downgrade ? (
                  <>
                    <Row label={<T _str="billed now" swc />} emphasis>
                      {formatPrice(0)}
                    </Row>
                    {preview.next && (
                      <Row label={<T _str="from {date}" args={{ date: renewal ?? '' }} swc />}>
                        <T _str="{price} / year" args={{ price: formatPrice(preview.next.yearlyPriceCents) }} />
                      </Row>
                    )}
                  </>
                ) : (
                  <>
                    {preview.lines.map((line) => (
                      <Row key={`${line.subscriptionType}-${line.from}`} label={<T _str="{plan} · {months} month(s)" args={{ plan: plans[line.subscriptionType], months: line.months }} />}>
                        {formatPrice(line.totalCents)}
                      </Row>
                    ))}
                    {preview.currentTermValueCents > 0 && <Row label={<T _str="already paid for this term" swc />}>− {formatPrice(preview.currentTermValueCents)}</Row>}
                    <Row label={<T _str="billed now" swc />} emphasis className="border-t border-gray-200 pt-1 dark:border-gray-700">
                      {formatPrice(preview.chargedCents)}
                    </Row>
                    <Hint>{upgrade ? <T _str="You pay the new plan only for the months left in your term. Prices exclude VAT." /> : <T _str="A year of {plan} from today. Prices exclude VAT." args={{ plan }} />}</Hint>
                  </>
                )}
              </Section>
              {paused && (
                <Hint className="text-warning-yellow-700 dark:text-warning-yellow-500">
                  <T _str="Your subscription is paused: the plan can be changed once it resumes." />
                </Hint>
              )}
              {billing?.enabled && !downgrade && !keeps && <BillingInfoCard billing={billing} onEdit={openBillingModal} />}
              {sells && (
                <label className={classNames('flex cursor-pointer items-center gap-2 rounded-md border p-3 text-xs', agreed ? 'border-gray-300 dark:border-gray-700' : 'border-warning-yellow-500')}>
                  <CheckBox checked={agreed} onChange={onAgree} />
                  <span>
                    <T _str="I agree to the" />{' '}
                    <a href={POINTORAMA_TERMS_URL} target="_blank" rel="noreferrer" className="underline">
                      <T _str="terms and conditions" />
                    </a>{' '}
                    <T _str="for this order" />.
                  </span>
                </label>
              )}
            </Modal.Body>
            <Modal.Footer className="justify-between">
              <Button variant="secondary" onClick={close}>
                <T _str="cancel" swc />
              </Button>
              <Button variant="primary" onClick={onConfirm} loading={loading} disabled={loading || previewLoading || paused || (sells && (billingMissing || !agreed)) || preview.problems.length > 0}>
                {keeps ? (
                  <T _str="keep {plan}" args={{ plan }} swc />
                ) : downgrade ? (
                  <T _str="switch at renewal" swc />
                ) : starts ? (
                  <T _str="start {plan} · {price}" args={{ plan, price: formatPrice(preview.chargedCents) }} swc />
                ) : (
                  <T _str="upgrade now · {price}" args={{ price: formatPrice(preview.chargedCents) }} swc />
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal.Container>
      {billing?.enabled && <BillingInfoModal open={billingModalOpen} onClose={closeBillingModal} organisationId={organisationId} info={billing?.info} />}
      <ContactSalesModal open={contactOpen} onClose={closeContact} organisationId={organisationId} />
    </>
  );
};

// Who each plan is for, and the support that comes with it — the pricing page's words.
const usePlanCopy = () => {
  const pitch: Record<SubscriptionType, string> = {
    [SubscriptionType.Launch]: useT('For professionals'),
    [SubscriptionType.Grow]: useT('For teams'),
    [SubscriptionType.Scale]: useT('For large organizations'),
    [SubscriptionType.Flex]: '',
    [SubscriptionType.Custom]: '',
  };
  const support: Record<SubscriptionType, string> = {
    [SubscriptionType.Launch]: useT('Support: Tutorials & e-mail'),
    [SubscriptionType.Grow]: useT('Support: Tutorials & e-mail'),
    [SubscriptionType.Scale]: useT('Support: Tutorials, e-mail & chat'),
    [SubscriptionType.Flex]: '',
    [SubscriptionType.Custom]: '',
  };
  return { pitch, support };
};
// The plan put forward (outlined in the accent, the filled button): on one of the plans, the next one up, as
// an upgrade; on anything else (FLEX, a custom deal), the most popular one.
const RECOMMENDED = SubscriptionType.Grow;
type Highlight = 'upgrade' | 'popular';

// Whole euros for the headline price: "€ 124 / month".
const wholeEuros = (cents: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(cents / 100));

const Feature: React.FC2 = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon-green-600" strokeWidth={2} />
    <span>{children}</span>
  </li>
);

// One plan, as a card the way the pricing page shows it: the name, who it is for, the price a month, and
// what comes with it as a list — and the one thing to do with it: start it, upgrade to it, or (a smaller
// one) switch at renewal, with what that costs said underneath. The current plan is marked, not offered;
// the recommended one is outlined in the app's accent.
const PlanCard: React.FC<{ organisationId: string; subscriptionType: SubscriptionType; highlight?: Highlight; onPick: (preview: Preview) => void }> = ({ organisationId, subscriptionType, highlight, onPick }) => {
  const plans = useSubscriptionTypeTranslations();
  const copy = usePlanCopy();
  const { formatDay } = useFormatDate();
  const { data, previousData } = usePlanPreview(organisationId, subscriptionType);
  const preview = data?.organisationPlanChangePreview ?? previousData?.organisationPlanChangePreview;
  const current = preview?.kind === PlanChangeKind.Unchanged;
  const recommended = !current && !!highlight;
  // A change already waiting at the end of the term: this plan is the one scheduled, or (on the current
  // plan's card) there is one to call off.
  const scheduledType = preview?.current.scheduledType ?? null;
  const scheduledAt = preview?.current.scheduledAt ? formatDay(dayAfterTermEnd(preview.current.scheduledAt)) : undefined;
  const scheduled = !current && scheduledType === subscriptionType;
  const keeps = current && !!scheduledType;
  // A cheaper plan waits at the end of the term: the preview's `after` is the term as it stands, the plan
  // itself is `next` — the term that follows.
  const state = preview?.kind === PlanChangeKind.ScheduledDowngrade ? preview.next : preview?.after;
  const pill = 'inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest';
  return (
    <div
      className={classNames(
        'flex flex-col gap-5 rounded-lg border bg-white p-6 dark:bg-eerie-black',
        current ? 'border-neon-green-500' : scheduled ? 'border-warning-yellow-500' : recommended ? 'border-french-violet-600 shadow-md' : 'border-gray-300 dark:border-gray-600',
      )}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-lg font-semibold">{plans[subscriptionType]}</span>
          {current ? (
            <span className={classNames(pill, 'bg-neon-green-500 text-black')}>
              <T _str="your plan" />
            </span>
          ) : scheduled ? (
            <span className={classNames(pill, 'bg-warning-yellow-500 text-black')}>
              <T _str="scheduled" />
            </span>
          ) : recommended ? (
            <span className={classNames(pill, 'bg-french-violet-600 text-white')}>{highlight === 'upgrade' ? <T _str="upgrade" /> : <T _str="most popular" />}</span>
          ) : null}
        </div>
        <span className="text-sm text-gray-500">{copy.pitch[subscriptionType]}</span>
      </div>
      {state ? (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-semibold tracking-tight">{wholeEuros(state.yearlyPriceCents / 12)}</span>
            <span className="text-sm text-gray-500">
              / <T _str="month" />
            </span>
          </div>
          <ul className="flex flex-col gap-2.5">
            <Feature>
              <T _str="{credits} projects/month" args={{ credits: state.monthlyCredits }} />
            </Feature>
            <Feature>
              <T _str="{price} per extra project" args={{ price: formatPrice(state.projectUnitPriceCents) }} />
            </Feature>
            <Feature>
              <T _str="Billed {price}/year" args={{ price: formatPrice(state.yearlyPriceCents) }} />
            </Feature>
            <Feature>{copy.support[subscriptionType]}</Feature>
          </ul>
        </>
      ) : (
        <div className="flex h-48 items-center justify-center">
          <CircleSpinner className="h-5 w-5" />
        </div>
      )}
      {/* The same slot on every card — a button-high row and two lines under it — so the actions line up across cards. */}
      {preview && (current ? keeps : true) && (
        <div className="mt-auto flex flex-col gap-1.5 pt-2">
          <div className="flex h-12 items-stretch">
            {keeps ? (
              <Button variant="secondary" className="m-0! w-full" onClick={() => onPick(preview)}>
                <T _str="keep {plan}" args={{ plan: plans[subscriptionType] }} swc />
              </Button>
            ) : scheduled ? (
              <span className="flex w-full items-center justify-center rounded-xl border-2 border-warning-yellow-500 text-base font-medium text-warning-yellow-700 dark:text-warning-yellow-500">
                <T _str="Your plan from {date}" args={{ date: scheduledAt ?? '' }} />
              </span>
            ) : preview.kind === PlanChangeKind.ScheduledDowngrade ? (
              <Button variant="secondary" className="m-0! w-full" onClick={() => onPick(preview)}>
                <T _str="switch at renewal" swc />
              </Button>
            ) : (
              // The filled button on the recommended plan only; the others are outlined.
              <Button variant={recommended ? 'primary' : 'secondary'} className="m-0! w-full" onClick={() => onPick(preview)}>
                <T _str="choose {plan}" args={{ plan: plans[subscriptionType] }} swc />
              </Button>
            )}
          </div>
          <span className={classNames('min-h-8 text-center text-xs leading-4', keeps ? 'text-warning-yellow-700 dark:text-warning-yellow-500' : 'text-gray-500')}>
            {keeps ? (
              <T _str="Becomes {plan} on {date}. Keep it to cancel that" args={{ plan: scheduledType ? plans[scheduledType] : '', date: scheduledAt ?? '' }} />
            ) : scheduled ? (
              <T _str="Until then nothing changes" />
            ) : preview.kind === PlanChangeKind.ScheduledDowngrade ? (
              <T _str="Nothing changes until {date}" args={{ date: formatDay(dayAfterTermEnd(preview.effectiveAt)) }} />
            ) : preview.kind === PlanChangeKind.NewSubscription ? (
              <T _str="Billed yearly, starts today" />
            ) : (
              <T _str="Billed yearly" />
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export interface PlanPickerProps {
  organisationId: string;
  // The plan the organisation is on: the cards above it are upgrades, the ones below are kept out of the way.
  currentType: SubscriptionType;
}

// The customer's own plan change, on the subscription page: the bigger plans up front, the smaller ones a
// click away, each with what it gives and what moving to it costs — and the confirmation with the reasoning.
const PlanPicker_: React.FC2<PlanPickerProps> = ({ organisationId, currentType, className }) => {
  const [picked, setPicked] = useState<Preview | undefined>(undefined);
  const [showSmaller, setShowSmaller] = useState(false);
  const { open, onOpen, onClose } = useOpen();
  const onPick = useCallback(
    (preview: Preview) => {
      setPicked(preview);
      onOpen();
    },
    [onOpen],
  );
  const onChanged = useCallback(() => setShowSmaller(false), []);
  const currentIndex = PLANS.indexOf(currentType as (typeof PLANS)[number]);
  // On a plan of ours, the smaller ones wait behind a link; on anything else (FLEX, a custom deal) every plan shows.
  const upFront = currentIndex >= 0 ? PLANS.slice(currentIndex) : [...PLANS];
  const smaller = currentIndex > 0 ? PLANS.slice(0, currentIndex) : [];
  const shown = showSmaller ? [...smaller, ...upFront] : upFront;
  // Nothing above the top plan: then no card is put forward.
  const highlighted = currentIndex >= 0 ? PLANS[currentIndex + 1] : RECOMMENDED;
  const highlight: Highlight = currentIndex >= 0 ? 'upgrade' : 'popular';

  return (
    <div className={classNames('flex flex-col gap-4', className)}>
      <div className="flex flex-col">
        <span className="text-base font-semibold">{currentIndex >= 0 ? <T _str="Upgrade your plan" /> : <T _str="Choose a plan" />}</span>
        <span className="text-sm text-gray-500">
          <T _str="Upgrade anytime. Pay only the difference." />
        </span>
      </div>
      <div className={classNames('grid gap-4', shown.length > 1 && 'sm:grid-cols-2', shown.length > 2 && 'lg:grid-cols-3')}>
        {shown.map((subscriptionType) => (
          <PlanCard key={subscriptionType} organisationId={organisationId} subscriptionType={subscriptionType} highlight={subscriptionType === highlighted ? highlight : undefined} onPick={onPick} />
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-[0.2em] text-gray-500">
          <T _str="prices excl. VAT · upgrade any time · cancel anytime" />
        </span>
        {smaller.length > 0 && !showSmaller && (
          <button type="button" onClick={() => setShowSmaller(true)} className="text-xs text-gray-500 underline hover:text-black dark:hover:text-white">
            <T _str="Need a smaller plan?" />
          </button>
        )}
      </div>
      {picked && <ChangePlanDialog open={open} onClose={onClose} organisationId={organisationId} preview={picked} onChanged={onChanged} />}
    </div>
  );
};

export const PlanPicker = memo(PlanPicker_);
